/**
 * materialRequest — "Need Something?" on the crew's phone (MATERIAL-REQ-01,
 * Seth 2026-09-25). Two pieces, on the job's Scope step (and the next-install
 * preview, so a crew can ask the night before):
 *
 *   · JobMaterialsCard — the job's requested materials with where each one is
 *     (requested / being pulled / ready on a shelf / on order / picked up),
 *     live as the warehouse works them, plus what the office planned.
 *   · NeedSomethingSheet — type or dictate it the way you'd say it → "Read It"
 *     → Pegasus lists what it read (tap a line to fix it, "Which one?" when a
 *     size is missing, "kept as typed" when nothing in the catalog fits) → when
 *     do you need it (Today · Tomorrow AM · By a Date · Just Order It) → a note
 *     → Send to the Warehouse.
 * Data: features/jobs/materialRequests.ts.
 */
import { useMemo, useRef, useState } from "react";
import { Camera, Check, ChevronRight, Loader2, PackageCheck, PackageSearch, Plus, Send, Sparkles, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { haptic } from "@/native/boot";
import { InfoTip } from "@/components/InfoTip";
import { NotesEditor } from "@/components/NotesEditor";
import { useJobMaterials, useJobVisits, type Job, type JobMaterial } from "@/features/jobs/jobs";
import { uploadOneFile } from "@/features/files/filesCore";
import {
  URGENCIES, URGENCY_LABEL, parseMaterialRequest, needByFor, lineStatusText,
  useJobMaterialRequests, useCreateMaterialRequest, useSetLineStatus,
  type MaterialRequest, type ParsedLine, type Urgency, type CatalogPick,
} from "@/features/jobs/materialRequests";
import { Sheet, card, input } from "./installWork";

const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const whenText = (iso: string) => new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
const dayText = (iso: string | null) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";

const STATUS_TONE: Record<string, string> = {
  requested: "text-slate-500", pulling: "text-amber-700", ready: "text-emerald-700",
  ordered: "text-sky-700", picked_up: "text-slate-400", cancelled: "text-slate-400 line-through",
};

/* ------------------------------------------------------------- the card */

export function JobMaterialsCard({ job, dispatchId, empno, techFirst }: {
  job: Job; dispatchId: string | null; empno: string | null; techFirst: string | null;
}) {
  const { data: reqs } = useJobMaterialRequests(job.id);
  const { data: mats } = useJobMaterials(job.id);
  // "Just Order It" = for the job start: the next scheduled visit on the job.
  const { data: visits } = useJobVisits(job.id);
  const today = new Date().toLocaleDateString("en-CA");
  const installDate = (visits ?? []).map((v) => v.scheduled_date).filter((d): d is string => !!d && d >= today).sort()[0] ?? null;
  const setStatus = useSetLineStatus();
  const [open, setOpen] = useState(false);
  const planned = (mats ?? []).filter((m) => (m.source ?? "office") === "office");
  const requests = (reqs ?? []).filter((r) => r.status !== "cancelled");
  const live = requests.flatMap((r) => r.lines).filter((l) => l.pull_status !== "cancelled");
  const ready = live.filter((l) => l.pull_status === "ready" || l.pull_status === "picked_up").length;

  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Materials for This Job</h4>
        <InfoTip label="How requests work">Ask for anything the job needs — the warehouse pulls it or orders it, and each line here shows where it is. Nothing here shows our cost.</InfoTip>
        {live.length > 0 && <span className={cn("m-chip ml-auto", ready === live.length && "m-chip-good")}>{ready} of {live.length} ready</span>}
      </div>

      {planned.length > 0 && (
        <div className="mt-2">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Planned by the office</div>
          <ul className="mt-1 divide-y divide-slate-100">
            {planned.map((m) => (
              <li key={m.id} className="flex items-center gap-2 py-1.5">
                <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-slate-800">{m.description}</span>
                <span className="shrink-0 text-[12px] font-semibold text-slate-500">{qtyText(Number(m.qty))}{m.unit ? ` ${m.unit}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {requests.map((r) => <RequestBlock key={r.id} r={r} mine={!!empno && r.requested_by_empno === empno}
        onPickedUp={(ids) => setStatus.mutate({ ids, status: "picked_up" }, { onSuccess: () => haptic("success") })} busy={setStatus.isPending} />)}

      {requests.length === 0 && planned.length === 0 && (
        <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-[12.5px] text-slate-500">Nothing requested yet. Short on something? Ask — the warehouse pulls it or orders it.</p>
      )}

      <button type="button" onClick={() => { haptic("light"); setOpen(true); }} className="m-btn m-btn-tint m-btn-big mt-3 w-full">
        <Plus className="h-[18px] w-[18px]" /> Need Something?
      </button>
      {setStatus.isError && <p className="mt-1.5 text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
      {open && <NeedSomethingSheet job={job} dispatchId={dispatchId} empno={empno} techFirst={techFirst} installDate={installDate} onClose={() => setOpen(false)} />}
    </section>
  );
}

function RequestBlock({ r, mine, onPickedUp, busy }: { r: MaterialRequest; mine: boolean; onPickedUp: (ids: string[]) => void; busy: boolean }) {
  const readyIds = r.lines.filter((l) => l.pull_status === "ready").map((l) => l.id);
  const who = mine ? "Your Request" : `${(r.requested_by_name ?? "The crew").split(/\s+/)[0]}'s Request`;
  return (
    <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-extrabold text-slate-700">{who} · {URGENCY_LABEL[r.urgency]}{r.urgency === "by_date" && r.need_by ? ` ${dayText(r.need_by)}` : ""}</span>
        <span className="shrink-0 text-[11px] text-slate-400">{whenText(r.created_at)}</span>
      </div>
      <ul className="mt-1 space-y-1">
        {r.lines.map((l) => (
          <li key={l.id} className="flex items-start gap-2">
            <span className="min-w-0 flex-1 text-[13.5px] leading-snug">
              <span className={cn("font-semibold", l.pull_status === "cancelled" ? "text-slate-400 line-through" : "text-slate-800")}>
                {qtyText(Number(l.qty))}{l.unit && l.unit !== "ea" ? ` ${l.unit}` : " ×"} {l.description}
              </span>
              <span className={cn("block text-[11.5px] font-semibold", STATUS_TONE[l.pull_status ?? "requested"])}>{lineStatusText(l)}</span>
            </span>
          </li>
        ))}
      </ul>
      {r.note && <p className="mt-1.5 text-[12px] text-slate-500">{mine ? "Your note" : "Note"}: “{r.note}”</p>}
      {readyIds.length > 0 && (
        <button type="button" disabled={busy} onClick={() => onPickedUp(readyIds)} className="m-btn m-btn-good mt-2 w-full">
          <PackageCheck className="h-4 w-4" /> Got It — Picked Up{readyIds.length < r.lines.length ? ` (${readyIds.length})` : ""}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ the sheet */

interface Draft { key: string; typed: string; qty: number; unit: string; name: string; question: string; sizes: string[]; pick: CatalogPick | null; options: CatalogPick[]; learn: boolean }

export function NeedSomethingSheet({ job, dispatchId, empno, techFirst, installDate, onClose }: {
  job: Job; dispatchId: string | null; empno: string | null; techFirst: string | null; installDate: string | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const create = useCreateMaterialRequest();
  const [text, setText] = useState("");
  const [reading, setReading] = useState(false);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [lines, setLines] = useState<Draft[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [urgency, setUrgency] = useState<Urgency | null>(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const read = async () => {
    const t = text.trim();
    if (!t) return;
    setReading(true);
    setReadErr(null);
    try {
      const got = await parseMaterialRequest(t);
      const drafts: Draft[] = got.map((l: ParsedLine, i) => ({
        key: `${Date.now()}-${i}`, typed: l.typed, qty: l.qty, unit: l.unit, name: l.name,
        question: l.question, sizes: l.sizes, pick: l.match, options: l.options, learn: false,
      }));
      // Anything already on the list stays; what was just read is added under it.
      setLines((cur) => [...(cur ?? []), ...drafts]);
      setText("");
      haptic("success");
      if (!drafts.length) setReadErr("Pegasus didn't find anything to order in that — try saying it another way.");
    } catch (e) {
      setReadErr((e as Error).message);
    } finally {
      setReading(false);
    }
  };

  const patch = (key: string, p: Partial<Draft>) => setLines((cur) => (cur ?? []).map((d) => (d.key === key ? { ...d, ...p } : d)));
  const remove = (key: string) => setLines((cur) => (cur ?? []).filter((d) => d.key !== key));
  const needBy = urgency ? needByFor(urgency, date || null, installDate) : null;
  const ok = !!lines?.length && !!urgency && (urgency !== "by_date" || !!date) && !create.isPending && !uploading;

  const onPhoto = async (f: File | null | undefined) => {
    if (!f) return;
    setUploading(true);
    try {
      const file = await uploadOneFile({ custno: job.custno, locno: job.locno, job_id: job.id, caption: "Material request", doc_tag: "photo", origin: "tech" }, f);
      setPhoto({ id: file.id, name: f.name });
      void qc.invalidateQueries({ queryKey: ["files"] });
    } catch {
      setReadErr("The photo didn't upload — check the signal and try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = () => {
    if (!ok || !lines || !urgency) return;
    create.mutate({
      jobId: job.id, dispatchId, urgency, needBy, typed: lines.map((l) => l.typed).join("; "), note,
      lines: lines.map((l) => ({
        description: l.pick?.description ?? l.name, qty: l.qty, unit: l.unit || null,
        item_id: l.pick?.item_id ?? null, typed: l.typed, learn: l.learn,
      })),
      empno, name: techFirst, photoFileId: photo?.id ?? null,
    }, { onSuccess: () => { haptic("success"); onClose(); } });
  };

  return (
    <Sheet title="Need Something?" sub={[job.customer_name, job.title].filter(Boolean).join(" · ")} onClose={onClose}
      footer={
        <button type="button" disabled={!ok} onClick={send} className="m-btn m-btn-primary m-btn-big w-full">
          {create.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Send className="h-[18px] w-[18px]" />}
          {!lines?.length ? "Read it first" : !urgency ? "When do you need it?" : "Send to the Warehouse"}
        </button>
      }>
      <section className={card}>
        <h4 className="text-[14px] font-bold text-slate-900">What Do You Need?</h4>
        <p className="text-[12px] text-slate-500">Type it the way you&apos;d say it — or tap Dictate.</p>
        <div className="mt-2">
          <NotesEditor value={text} onChange={setText} rows={3} autoTidy={false}
            placeholder="e.g. 3 3/4 90s, 10 ft 3/8 x 7/8 line set, a disconnect, 2 cans of foam" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button type="button" disabled={!text.trim() || reading} onClick={read} className="m-btn m-btn-primary">
            {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {reading ? "Reading…" : lines?.length ? "Read & Add" : "Read It"}
          </button>
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="m-btn m-btn-soft">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : photo ? <Check className="h-4 w-4 text-emerald-600" /> : <Camera className="h-4 w-4" />} {photo ? "Photo Added" : "Add a Photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void onPhoto(e.target.files?.[0])} />
        </div>
        {readErr && <p className="mt-2 text-[12px] font-semibold text-rose-600">{readErr}</p>}
      </section>

      {lines && lines.length > 0 && (
        <section className={card}>
          <div className="flex items-center gap-2">
            <h4 className="text-[14px] font-bold text-slate-900">Pegasus Read It As</h4>
            <span className="ml-auto text-[11.5px] text-slate-400">Tap a line to fix it</span>
          </div>
          <ul className="mt-1.5 divide-y divide-slate-100">
            {lines.map((d) => (
              <DraftRow key={d.key} d={d} editing={editing === d.key}
                onEdit={() => setEditing(editing === d.key ? null : d.key)}
                onPatch={(p) => patch(d.key, p)} onRemove={() => remove(d.key)} />
            ))}
          </ul>
        </section>
      )}

      {lines && lines.length > 0 && (
        <section className={card}>
          <h4 className="text-[14px] font-bold text-slate-900">When Do You Need It?</h4>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {URGENCIES.map((u) => (
              <button key={u.v} type="button" aria-pressed={urgency === u.v} onClick={() => { haptic("light"); setUrgency(u.v); }}
                className={cn("rounded-2xl px-3 py-2.5 text-left [touch-action:manipulation]",
                  urgency === u.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800 active:bg-slate-200")}>
                <span className="block text-[14px] font-bold">{u.label}</span>
                <span className={cn("block text-[11.5px]", urgency === u.v ? "text-white/70" : "text-slate-500")}>
                  {u.v === "order" && installDate ? `For the job start · ${dayText(installDate)}` : u.sub}
                </span>
              </button>
            ))}
          </div>
          {urgency === "by_date" && (
            <input type="date" value={date} min={new Date().toLocaleDateString("en-CA")} onChange={(e) => setDate(e.target.value)} className={cn(input, "mt-2")} aria-label="Need it by" />
          )}
          <label className="mt-3 block">
            <span className="text-[12.5px] font-bold text-slate-700">Note for the Warehouse</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Leave it on my cart by door 2" className={cn(input, "mt-1")} />
          </label>
        </section>
      )}
      {create.isError && <p className="text-center text-[12px] font-semibold text-rose-600">{(create.error as Error).message}</p>}
    </Sheet>
  );
}

function DraftRow({ d, editing, onEdit, onPatch, onRemove }: {
  d: Draft; editing: boolean; onEdit: () => void; onPatch: (p: Partial<Draft>) => void; onRemove: () => void;
}) {
  const label = d.pick?.description ?? d.name;
  const status = d.pick
    ? { text: d.pick.in_stock ? "Matched · in stock at the shop" : d.pick.learned ? "Matched · what the crew calls it" : "Matched", tone: "text-emerald-700" }
    : d.options.length || d.sizes.length
      ? { text: d.question || "Which one?", tone: "text-amber-700" }
      : { text: "No match — kept as typed. The warehouse reads it.", tone: "text-slate-500" };
  const chooseSize = (s: string) => onPatch({ name: /line\s*set/i.test(d.name) ? `Line Set ${s}` : `${d.name} — ${s}`, sizes: [], question: "" });
  return (
    <li className="py-2">
      <button type="button" onClick={onEdit} className="flex w-full items-start gap-2 text-left active:opacity-70">
        <span className="w-10 shrink-0 text-right text-[14px] font-extrabold tabular-nums text-slate-900">{qtyText(d.qty)}{d.unit && d.unit !== "ea" ? <span className="text-[11px] font-bold text-slate-500"> {d.unit}</span> : "×"}</span>
        <span className="min-w-0 flex-1">
          <span className={cn("block text-[14px] font-semibold leading-snug", d.pick ? "text-slate-800" : "text-slate-700")}>{d.pick ? label : `“${d.typed || d.name}”`}</span>
          {!d.pick && d.name && d.name.toLowerCase() !== d.typed.toLowerCase() && <span className="block text-[11.5px] text-slate-500">Read as: {d.name}</span>}
          <span className={cn("block text-[11.5px] font-semibold", status.tone)}>{status.text}</span>
        </span>
        <ChevronRight className={cn("mt-1 h-4 w-4 shrink-0 text-slate-300 transition-transform", editing && "rotate-90")} />
      </button>
      {!d.pick && (d.options.length > 0 || d.sizes.length > 0) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5 pl-12">
          {d.options.map((o) => (
            <button key={o.item_id} type="button" onClick={() => { haptic("light"); onPatch({ pick: o, learn: true, options: [], sizes: [], question: "" }); }}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 active:bg-slate-100">
              {o.description ?? o.part_number}
            </button>
          ))}
          {!d.options.length && d.sizes.map((s) => (
            <button key={s} type="button" onClick={() => { haptic("light"); chooseSize(s); }}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 active:bg-slate-100">
              {s}
            </button>
          ))}
        </div>
      )}
      {editing && (
        <div className="mt-2 space-y-2 rounded-2xl bg-slate-50 p-2.5 pl-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => onPatch({ qty: Math.max(0.5, Math.round((d.qty - 1) * 100) / 100) })} className="m-btn m-btn-soft !h-9 !w-9 !p-0" aria-label="Less">−</button>
            <input value={qtyText(d.qty)} inputMode="decimal" aria-label="How many"
              onChange={(e) => { const n = Number(e.target.value.replace(/[^0-9.]/g, "")); if (Number.isFinite(n) && n > 0) onPatch({ qty: n }); }}
              className={cn(input, "!w-20 text-center")} />
            <button type="button" onClick={() => onPatch({ qty: Math.round((d.qty + 1) * 100) / 100 })} className="m-btn m-btn-soft !h-9 !w-9 !p-0" aria-label="More">+</button>
            <input value={d.unit} onChange={(e) => onPatch({ unit: e.target.value })} placeholder="ea, ft…" aria-label="Unit" className={cn(input, "!w-20")} />
          </div>
          <input value={d.pick ? d.pick.description ?? "" : d.name} aria-label="What it is"
            onChange={(e) => onPatch({ pick: null, name: e.target.value, options: [], sizes: [], question: "" })}
            className={input} />
          <div className="flex gap-2">
            {d.pick && <button type="button" onClick={() => onPatch({ pick: null })} className="m-btn m-btn-soft flex-1">Not That One</button>}
            <button type="button" onClick={onRemove} className="m-btn m-btn-soft flex-1 !text-rose-600"><X className="h-4 w-4" /> Remove</button>
          </div>
        </div>
      )}
    </li>
  );
}

/** Lines the crew can use on the Changes step (requested lines that weren't cancelled). */
export function requestLinesUsed(mats: JobMaterial[]): JobMaterial[] {
  return mats.filter((m) => m.source === "request" && m.pull_status !== "cancelled");
}

export function useOpenRequestCount(jobId: string) {
  const { data } = useJobMaterialRequests(jobId);
  return useMemo(() => (data ?? []).flatMap((r) => r.lines).filter((l) => l.pull_status === "requested" || l.pull_status === "pulling" || l.pull_status === "ordered").length, [data]);
}
